import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from '../../axios';

const STATUS_LABELS = {
  active: '有効',
  stopped: '停止',
  reached: '上限到達',
  no_balance: '停止（残高なし）',
  expired: '終了',
  unset: '未設定',
};

const PrPointManagement = ({ balance, nextExpiry, events, purchaseUrl, detailUrl, onDataChange }) => {
  const [editingLimits, setEditingLimits] = useState({});
  const [processing, setProcessing] = useState({});
  const [notification, setNotification] = useState(null);
  const [confirmingStop, setConfirmingStop] = useState(null);

  useEffect(
    function () {
      if (notification) {
        var timer = setTimeout(function () {
          setNotification(null);
        }, 4000);
        return function () {
          clearTimeout(timer);
        };
      }
    },
    [notification],
  );

  const showNotify = function (type, message) {
    setNotification({ type: type, message: message });
  };

  const handleLimitChange = (eventId, value) => {
    setEditingLimits(function (prev) {
      var next = Object.assign({}, prev);
      next[eventId] = value;
      return next;
    });
  };

  const getLimitValue = (ev) => {
    if (editingLimits[ev.event_id] !== undefined)
      return editingLimits[ev.event_id];
    if (ev.limit !== null && ev.consumed > ev.limit)
      return String(ev.consumed + 1);
    return ev.limit !== null ? String(ev.limit) : '';
  };

  const handleUpdateLimit = async (ev) => {
    var key = 'update_' + ev.event_id;
    setProcessing(function (prev) {
      var n = Object.assign({}, prev);
      n[key] = true;
      return n;
    });
    try {
      var rawValue = getLimitValue(ev);
      var limitValue = rawValue === '' ? null : parseInt(rawValue, 10);
      await axios.post(
        '/api/events/' + ev.event_id + '/pr_limitations/update',
        {
          event_id: ev.event_id,
          limit: limitValue,
        },
      );
      showNotify('success', '上限を更新しました');
      if (onDataChange) onDataChange();
    } catch (e) {
      var msg =
        (e.response && e.response.data && e.response.data.error) ||
        '設定の更新に失敗しました';
      showNotify('error', msg);
    }
    setProcessing(function (prev) {
      var n = Object.assign({}, prev);
      n[key] = false;
      return n;
    });
  };

  const handleDeactivate = async (ev) => {
    var key = 'toggle_' + ev.event_id;
    setProcessing(function (prev) {
      var n = Object.assign({}, prev);
      n[key] = true;
      return n;
    });
    setConfirmingStop(null);
    try {
      await axios.post(
        '/api/events/' + ev.event_id + '/pr_limitations/deactivate',
        {
          event_id: ev.event_id,
        },
      );
      showNotify('success', 'ポイント利用を停止しました');
      if (onDataChange) onDataChange();
    } catch (e) {
      var msg =
        (e.response && e.response.data && e.response.data.error) ||
        '停止処理に失敗しました';
      showNotify('error', msg);
    }
    setProcessing(function (prev) {
      var n = Object.assign({}, prev);
      n[key] = false;
      return n;
    });
  };

  const handleActivate = async (ev) => {
    var key = 'toggle_' + ev.event_id;
    setProcessing(function (prev) {
      var n = Object.assign({}, prev);
      n[key] = true;
      return n;
    });
    try {
      var rawValue = getLimitValue(ev);
      var limitValue = rawValue === '' ? null : parseInt(rawValue, 10);
      await axios.post(
        '/api/events/' + ev.event_id + '/pr_limitations/update',
        {
          event_id: ev.event_id,
          limit: limitValue,
        },
      );
      showNotify('success', 'ポイント利用を再開しました');
      if (onDataChange) onDataChange();
    } catch (e) {
      var msg =
        (e.response && e.response.data && e.response.data.error) ||
        '再開処理に失敗しました';
      showNotify('error', msg);
    }
    setProcessing(function (prev) {
      var n = Object.assign({}, prev);
      n[key] = false;
      return n;
    });
  };

  return (
    <div className="o-dashboard__section">
      <h3 className="o-dashboard__section-title">PRポイント管理</h3>
      <div className="o-pr-point">
        {notification && (
          <div
            className={
              'o-pr-point__notification o-pr-point__notification--' +
              notification.type
            }
            role="alert"
            aria-live="polite"
          >
            <i
              className={
                'fas ' +
                (notification.type === 'success'
                  ? 'fa-check-circle'
                  : 'fa-exclamation-circle')
              }
            />
            <span>{notification.message}</span>
            <button
              type="button"
              className="o-pr-point__notification-close"
              onClick={function () {
                setNotification(null);
              }}
              aria-label="通知を閉じる"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        <div className="o-pr-point__header">
          <div className="o-pr-point__balance">
            <span className="o-pr-point__balance-label">残高</span>
            <span className="o-pr-point__balance-value">
              {Number(balance).toLocaleString()}
            </span>
            <span className="o-pr-point__balance-unit">pt</span>
            {nextExpiry && (
              <span className="o-pr-point__next-expiry">
                直近の期限 {nextExpiry.expired_on}（{Number(nextExpiry.points).toLocaleString()}pt）
              </span>
            )}
          </div>
          <div className="o-pr-point__header-links">
            {detailUrl && (
              <a href={detailUrl} className="o-pr-point__detail-link">
                有効期限・内訳 &rarr;
              </a>
            )}
            {purchaseUrl && (
              <a href={purchaseUrl} className="o-pr-point__purchase-link">
                PRチケット購入 &rarr;
              </a>
            )}
          </div>
        </div>

        {events.length > 0 && (
          <div className="o-pr-mgmt__list">
            {events.map(function (ev) {
              var isActive =
                ev.status === 'active' ||
                ev.status === 'reached' ||
                ev.status === 'no_balance';
              var isStopped = ev.status === 'stopped';
              var updateKey = 'update_' + ev.event_id;
              var toggleKey = 'toggle_' + ev.event_id;
              var hasLimit = ev.limit !== null && ev.limit !== undefined;
              var isOverLimit = hasLimit && ev.consumed > ev.limit;
              var progressPercent =
                hasLimit && ev.limit > 0
                  ? Math.min((ev.consumed / ev.limit) * 100, 100)
                  : 0;
              var progressLevel =
                progressPercent >= 85
                  ? 'danger'
                  : progressPercent >= 60
                    ? 'warning'
                    : 'normal';
              var isConfirmingThis = confirmingStop === ev.event_id;

              return (
                <div
                  key={ev.event_id}
                  className={
                    'o-pr-mgmt__card' +
                    (isStopped ? ' o-pr-mgmt__card--stopped' : '')
                  }
                >
                  <div className="o-pr-mgmt__card-header">
                    <div className="o-pr-mgmt__card-title">
                      <span className="o-pr-mgmt__event-name">
                        {ev.event_name}
                      </span>
                      {ev.event_date && (
                        <span className="o-pr-mgmt__event-date">
                          {ev.event_date}
                        </span>
                      )}
                    </div>
                    <span
                      className={
                        'o-pr-point__status o-pr-point__status--' + ev.status
                      }
                      role="status"
                      aria-label={
                        'ステータス: ' + (STATUS_LABELS[ev.status] || ev.status)
                      }
                    >
                      {STATUS_LABELS[ev.status] || ev.status}
                    </span>
                  </div>

                  {ev.status === 'no_balance' && (
                    <div className="o-pr-point__guidance o-pr-point__guidance--warning">
                      <span>
                        サークル残高が0のため、PRポイントが消費されていません。「PRチケット購入」から追加してください。
                      </span>
                    </div>
                  )}

                  <div className="o-pr-mgmt__info">
                    <span>
                      消費{' '}
                      <strong>{Number(ev.consumed).toLocaleString()}</strong>pt
                    </span>
                    <span className="o-pr-mgmt__info-sep">/</span>
                    <span>
                      上限{' '}
                      <strong>
                        {hasLimit
                          ? Number(ev.limit).toLocaleString()
                          : '無制限'}
                      </strong>
                      {hasLimit ? 'pt' : ''}
                    </span>
                    {isOverLimit && (
                      <span className="o-pr-mgmt__over-badge">上限超過</span>
                    )}
                  </div>

                  {hasLimit && (
                    <div className="o-pr-mgmt__progress">
                      <div
                        className="o-pr-mgmt__progress-track"
                        role="progressbar"
                        aria-valuenow={Math.round(progressPercent)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={
                          '消費進捗: ' + Math.round(progressPercent) + '%'
                        }
                      >
                        <div
                          className={
                            'o-pr-mgmt__progress-fill o-pr-mgmt__progress-fill--' +
                            progressLevel
                          }
                          style={{ width: progressPercent + '%' }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="o-pr-mgmt__card-actions">
                    <label
                      className="o-pr-mgmt__limit-label"
                      htmlFor={'mgmt-limit-' + ev.event_id}
                    >
                      上限:
                    </label>
                    <input
                      id={'mgmt-limit-' + ev.event_id}
                      type="number"
                      className="o-pr-point__input"
                      value={getLimitValue(ev)}
                      placeholder="無制限"
                      min={ev.consumed + 1}
                      disabled={
                        !isActive && !isStopped && ev.status !== 'unset'
                      }
                      onChange={function (e) {
                        handleLimitChange(ev.event_id, e.target.value);
                      }}
                    />
                    <span className="o-pr-point__input-unit">pt</span>
                    {(isActive || isStopped || ev.status === 'unset') && (
                      <button
                        type="button"
                        className="o-pr-point__btn o-pr-point__btn--primary"
                        onClick={function () {
                          handleUpdateLimit(ev);
                        }}
                        disabled={!!processing[updateKey]}
                        aria-label={
                          processing[updateKey] ? '更新処理中' : '上限を更新'
                        }
                      >
                        {processing[updateKey] ? (
                          <span>
                            <i className="fas fa-spinner fa-pulse" /> 更新
                          </span>
                        ) : (
                          '更新'
                        )}
                      </button>
                    )}
                  </div>

                  <div className="o-pr-mgmt__card-toggle">
                    {isActive && !isConfirmingThis && (
                      <button
                        type="button"
                        className="o-pr-point__btn o-pr-point__btn--danger-outline"
                        onClick={function () {
                          setConfirmingStop(ev.event_id);
                        }}
                        disabled={!!processing[toggleKey]}
                      >
                        停止
                      </button>
                    )}
                    {isActive && isConfirmingThis && (
                      <div className="o-pr-mgmt__confirm">
                        <span className="o-pr-mgmt__confirm-text">
                          <i className="fas fa-exclamation-triangle" />{' '}
                          停止しますか？
                        </span>
                        <div className="o-pr-mgmt__confirm-actions">
                          <button
                            type="button"
                            className="o-pr-point__btn o-pr-point__btn--danger"
                            onClick={function () {
                              handleDeactivate(ev);
                            }}
                            disabled={!!processing[toggleKey]}
                            aria-label={
                              processing[toggleKey] ? '停止処理中' : '停止する'
                            }
                          >
                            {processing[toggleKey] ? (
                              <span>
                                <i className="fas fa-spinner fa-pulse" /> 処理中
                              </span>
                            ) : (
                              '停止する'
                            )}
                          </button>
                          <button
                            type="button"
                            className="o-pr-point__btn o-pr-point__btn--ghost"
                            onClick={function () {
                              setConfirmingStop(null);
                            }}
                            disabled={!!processing[toggleKey]}
                          >
                            キャンセル
                          </button>
                        </div>
                      </div>
                    )}
                    {isStopped && (
                      <button
                        type="button"
                        className="o-pr-point__btn o-pr-point__btn--success"
                        onClick={function () {
                          handleActivate(ev);
                        }}
                        disabled={!!processing[toggleKey]}
                        aria-label={
                          processing[toggleKey]
                            ? '再開処理中'
                            : 'ポイント利用を再開'
                        }
                      >
                        {processing[toggleKey] ? (
                          <span>
                            <i className="fas fa-spinner fa-pulse" /> 処理中
                          </span>
                        ) : (
                          '再開'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {events.length === 0 && (
          <div className="o-pr-mgmt__empty">
            <i className="fas fa-info-circle" />
            <span>PRポイントを利用しているイベントはありません</span>
          </div>
        )}
      </div>
    </div>
  );
};

PrPointManagement.propTypes = {
  balance: PropTypes.number.isRequired,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      event_id: PropTypes.number,
      event_name: PropTypes.string,
      event_date: PropTypes.string,
      limit: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
      consumed: PropTypes.number,
      status: PropTypes.string,
    }),
  ).isRequired,
  purchaseUrl: PropTypes.string,
  detailUrl: PropTypes.string,
  nextExpiry: PropTypes.shape({
    expired_on: PropTypes.string,
    points: PropTypes.number,
  }),
  onDataChange: PropTypes.func,
};

export default PrPointManagement;
